<template>
    <div>
        <!-- Shopping Cart Floating Button -->
        <div class="cart-floating" @click="toggleCart">
            <div class="cart-icon">
                🛒
                <span v-if="cartTotalItems > 0 || hasPendingInvoice" class="cart-badge">
                    {{ cartTotalItems > 0 ? cartTotalItems : '!' }}
                </span>
            </div>
        </div>

        <!-- Shopping Cart Modal -->
        <div v-if="showCart" class="cart-modal-overlay" @click="closeCart">
            <div class="cart-modal-content" @click.stop>
                <div class="cart-modal-header">
                    <h3>🛒 {{ $t('sales.kalaStore.cart.title') }}</h3>
                    <button class="close-cart-btn" @click="closeCart">×</button>
                </div>
                
                <div class="cart-modal-body">
                    <div v-if="isProcessing" class="processing-overlay">
                        <div class="spinner"></div>
                        <p>در حال ثبت سفارش...</p>
                    </div>
                    
                    <div v-if="cartItems.length === 0" class="empty-cart">
                        <p>{{ $t('sales.kalaStore.cart.empty') }}</p>
                        <button class="continue-shopping" @click="closeCart">
                            {{ $t('sales.kalaStore.cart.continueShopping') }}
                        </button>
                    </div>
                    
                    <div v-else>
                        <div class="cart-items">
                            <div v-for="(item, index) in cartItems" :key="index" class="cart-item">
                                <div class="cart-item-info">
                                    <div class="cart-item-name">{{ item.name }}</div>
                                    <div v-if="item.price != 0" class="cart-item-price">{{ formatCurrency(item.price) }} {{ $t('sales.kalaStore.common.rial') }}</div>
                                </div>
                                
                                <div class="cart-item-controls">
                                    <button class="quantity-btn minus" @click="decreaseQuantity(item)">−</button>
                                    <input 
                                        type="number" 
                                        v-model.number="item.quantity"
                                        min="1"
                                        :max="item.max_quantity || 999"
                                        @change="updateQuantity(item)"
                                        class="quantity-input"
                                    />
                                    <button class="quantity-btn plus" @click="increaseQuantity(item)">+</button>
                                    <button class="remove-from-cart-btn" @click="removeFromCart(item)" title="Remove from cart">×</button>
                                    <span class="product-unit">{{ getProductUnit(item) }}</span>
                                </div>
                                
                                <div class="cart-item-note">
                                    <input 
                                        type="text"
                                        v-model="item.note"
                                        @input="updateNote(item)"
                                        :placeholder="'توضیحات برای ' + item.name"
                                        class="note-input"
                                    />
                                    <span class="note-icon">📝</span>
                                </div>
                                
                                <div class="cart-item-total" v-if="(item.price * item.quantity) > 0">
                                    {{ $t('sales.kalaStore.cart.itemTotal') }}: {{ formatCurrency(item.price * item.quantity) }} {{ $t('sales.kalaStore.common.rial') }}
                                </div>
                            </div>
                        </div>
                        
                        <div class="cart-summary">
                            <div class="summary-row">
                                <span>{{ $t('sales.kalaStore.cart.totalItems') }}:</span>
                                <span>{{ cartTotalItems }} {{ $t('sales.kalaStore.common.unitCount') }}</span>
                            </div>
                            <div class="summary-row total" v-if="cartTotalAmount > 0">
                                <span>{{ $t('sales.kalaStore.cart.totalAmount') }}:</span>
                                <span>{{ formatCurrency(cartTotalAmount) }} {{ $t('sales.kalaStore.common.rial') }}</span>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div class="cart-modal-footer" v-if="cartItems.length > 0">
                    <button class="clear-cart-btn" @click="clearCart">{{ $t('sales.kalaStore.cart.clearCart') }}</button>
                    <button class="checkout-btn" @click="proceedToCheckout" :disabled="isProcessing">
                        {{ isProcessing ? 'در حال ثبت...' : $t('sales.kalaStore.cart.checkout') }}
                    </button>
                </div>
                
                <div v-if="showToast" class="toast-notification" :class="toastType">
                    <div class="toast-content">
                        <span class="toast-icon">{{ toastIcon }}</span>
                        <span class="toast-message">{{ toastMessage }}</span>
                        <button class="toast-close" @click="showToast = false">×</button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Registration Modal -->
        <div v-if="showRegistrationModal" class="registration-modal-overlay" @click="closeRegistrationModal">
            <div class="registration-modal-content" @click.stop>
                <div class="registration-modal-header">
                    <h3>📝 ثبت نام مشتری جدید</h3>
                    <button class="close-modal-btn" @click="closeRegistrationModal">×</button>
                </div>
                
                <div class="registration-modal-body">
                    <div class="registration-message">
                        <p>⚠️ شما در سیستم این فروشگاه ثبت نام نشده‌اید.</p>
                        <p>لطفاً اطلاعات زیر را برای ثبت نام وارد کنید:</p>
                    </div>
                    
                    <form @submit.prevent="submitRegistration" class="registration-form">
                        <div class="form-group">
                            <label for="customerName">نام و نام خانوادگی *</label>
                            <input 
                                id="customerName"
                                type="text" 
                                v-model="registrationData.name" 
                                placeholder="مثال: علی محمدی"
                                required
                                class="form-input"
                            />
                        </div>
                        
                        <div class="form-group">
                            <label for="customerPhone">شماره تلفن *</label>
                            <input 
                                id="customerPhone"
                                type="tel" 
                                v-model="registrationData.phone" 
                                placeholder="مثال: 09123456789"
                                required
                                class="form-input"
                                readonly
                            />
                            <small class="form-hint">شماره تلفن شما به صورت خودکار از حساب کاربری گرفته شده است</small>
                        </div>
                        
                        <div class="form-group">
                            <label for="customerNationalCode">کد ملی</label>
                            <input 
                                id="customerNationalCode"
                                type="text" 
                                v-model="registrationData.national_code" 
                                placeholder="مثال: 1234567890"
                                class="form-input"
                            />
                        </div>
                        
                        <div class="form-group">
                            <label for="customerAddress">آدرس</label>
                            <textarea 
                                id="customerAddress"
                                v-model="registrationData.address" 
                                placeholder="آدرس کامل خود را وارد کنید"
                                class="form-textarea"
                                rows="3"
                            ></textarea>
                        </div>
                        
                        <div class="form-group">
                            <label for="customerPostalCode">کد پستی</label>
                            <input 
                                id="customerPostalCode"
                                type="text" 
                                v-model="registrationData.postal_code" 
                                placeholder="مثال: 1234567890"
                                class="form-input"
                            />
                        </div>
                        
                        <div class="form-actions">
                            <button type="button" class="cancel-btn" @click="closeRegistrationModal">انصراف</button>
                            <button type="submit" class="register-btn" :disabled="registrationLoading">
                                {{ registrationLoading ? 'در حال ثبت...' : 'ثبت نام' }}
                            </button>
                        </div>
                    </form>
                </div>
                
                <div v-if="showToast" class="toast-notification" :class="toastType">
                    <div class="toast-content">
                        <span class="toast-icon">{{ toastIcon }}</span>
                        <span class="toast-message">{{ toastMessage }}</span>
                        <button class="toast-close" @click="showToast = false">×</button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Invoice Confirmation Modal -->
        <div v-if="showInvoiceConfirmationModal" class="cart-modal-overlay" @click="closeInvoiceConfirmationModal">
            <div class="invoice-confirmation-modal" @click.stop>
                <div class="modal-header">
                    <h3>📄 {{ $t('sales.kalaStore.invoiceConfirmation.title') || 'تایید فاکتور' }}</h3>
                    <button class="close-btn" @click="closeInvoiceConfirmationModal">×</button>
                </div>
                
                <div class="modal-body">
                    <div v-if="invoiceConfirmationLoading" class="loading-state">
                        <div class="spinner"></div>
                        <p>{{ $t('sales.kalaStore.invoiceConfirmation.loading') || 'در حال بارگذاری جزئیات...' }}</p>
                    </div>
                    
                    <div v-else-if="invoiceConfirmationData" class="invoice-confirmation-content">
                        <div class="confirmation-summary">
                            <div class="summary-row">
                                <span class="label">{{ $t('sales.kalaStore.invoiceConfirmation.invoiceNumber') || 'شماره فاکتور' }}:</span>
                                <span class="value">{{ invoiceConfirmationData.Num || invoiceConfirmationData.invoice_info?.invoice_id || '-' }}</span>
                            </div>
                            <div class="summary-row">
                                <span class="label">{{ $t('sales.kalaStore.invoiceConfirmation.date') || 'تاریخ' }}:</span>
                                <span class="value">{{ formatDate(invoiceConfirmationData.Dates) }}</span>
                            </div>
                            <div class="summary-row">
                                <span class="label">{{ $t('sales.kalaStore.invoiceConfirmation.totalItems') || 'تعداد اقلام' }}:</span>
                                <span class="value">{{ invoiceConfirmationData.details?.length || 0 }}</span>
                            </div>
                            <div class="summary-row total">
                                <span class="label">{{ $t('sales.kalaStore.invoiceConfirmation.totalAmount') || 'مبلغ کل' }}:</span>
                                <span class="value amount">{{ formatCurrency(invoiceConfirmationData.invoice_info?.total_amount || 0) }}</span>
                            </div>
                        </div>
                        
                        <div class="confirmation-items">
                            <h4>{{ $t('sales.kalaStore.invoiceConfirmation.items') || 'اقلام فاکتور' }}</h4>
                            <div class="items-list">
                                <div v-for="(item, index) in invoiceConfirmationData.details" :key="index" class="item-row">
                                    <span class="item-name">{{ item.KalaName || '-' }}</span>
                                    <span class="item-details">
                                        {{ item.value1 || 0 }} × {{ formatCurrency(item.Fe || 0) }}
                                    </span>
                                    <span class="item-total">{{ formatCurrency(item.money || 0) }}</span>
                                </div>
                            </div>
                        </div>
                        
                        <div class="terms-section">
                            <label class="terms-checkbox">
                                <input type="checkbox" v-model="termsAccepted" />
                                <span class="checkmark"></span>
                                <span class="terms-text">{{ $t('sales.kalaStore.invoiceConfirmation.agreeTerms') || 'با ثبت این فاکتور موافقت می‌کنم' }}</span>
                            </label>
                        </div>
                    </div>
                    
                    <div v-else-if="invoiceConfirmationError" class="error-state">
                        <div class="error-icon">⚠️</div>
                        <p>{{ invoiceConfirmationError }}</p>
                        <button class="retry-btn" @click="fetchInvoiceDetails">{{ $t('common.retry') || 'تلاش مجدد' }}</button>
                    </div>
                </div>
                
                <div class="cart-modal-footer">
                    <button class="cancel-btn" @click="closeInvoiceConfirmationModal">
                        {{ $t('common.cancel') || 'انصراف' }}
                    </button>
                    <button 
                        class="confirm-btn" 
                        @click="confirmInvoice" 
                        :disabled="!termsAccepted || invoiceConfirmationLoading"
                    >
                        <span v-if="invoiceConfirmationLoading" class="spinner-small"></span>
                        <span v-else>✓ {{ $t('sales.kalaStore.invoiceConfirmation.confirm') || 'تایید نهایی' }}</span>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { computed, ref, watch, onMounted } from 'vue'
import { invoiceService, authService, SalesService } from '@/services/api'
import moment from 'moment-jalaali'
import { useRoute } from 'vue-router'

export default {
    name: 'ShoppingCart',
    props: {
        externalCartItems: {
            type: Array,
            default: null
        }
    },
    emits: ['cart-updated', 'checkout', 'invoice-saved', 'customer-registered', 'invoice-confirmed'],
    setup(props, { emit }) {
        const route = useRoute()
        const cartItems = ref(JSON.parse(localStorage.getItem('shoppingCart') || '[]'))
        const showCart = ref(false)
        const isProcessing = ref(false)
        const showToast = ref(false)
        const toastMessage = ref('')
        const toastType = ref('success')
        const toastIcon = ref('✅')
        
        // ============= ACCOUNTANT MODE DETECTION =============
        const isAccountantMode = computed(() => {
            return route.path.includes('/accountant/')
        })
        
        // Get accountant-specific IDs from localStorage
        const getAccountantOperatorId = () => {
            return localStorage.getItem('accountant_operator_id')
        }
        
        const getAccountantCustomerId = () => {
            return localStorage.getItem('accountant_customer_id')
        }
        
        // ============= REGISTRATION MODAL =============
        const showRegistrationModal = ref(false)
        const registrationLoading = ref(false)
        const registrationData = ref({
            name: '',
            phone: '',
            national_code: '',
            address: '',
            postal_code: ''
        })
        
        // ============= INVOICE CONFIRMATION MODAL =============
        const showInvoiceConfirmationModal = ref(false)
        const invoiceConfirmationLoading = ref(false)
        const invoiceConfirmationData = ref(null)
        const invoiceConfirmationError = ref(null)
        const termsAccepted = ref(false)
        const pendingInvoiceId = ref(null)
        
        // ============= PENDING INVOICE TRACKING =============
        const PENDING_INVOICE_KEY = 'pendingInvoiceData'
        const pendingInvoice = ref(null)
        
        const hasPendingInvoice = computed(() => {
            return pendingInvoice.value !== null
        })
        
        const savePendingInvoice = (invoiceData) => {
            if (invoiceData) {
                localStorage.setItem(PENDING_INVOICE_KEY, JSON.stringify(invoiceData))
                pendingInvoice.value = invoiceData
            } else {
                localStorage.removeItem(PENDING_INVOICE_KEY)
                pendingInvoice.value = null
            }
        }
        
        const loadPendingInvoice = () => {
            try {
                const saved = localStorage.getItem(PENDING_INVOICE_KEY)
                if (saved) {
                    const data = JSON.parse(saved)
                    const now = Date.now()
                    const savedTime = data.timestamp || 0
                    if (now - savedTime < 24 * 60 * 60 * 1000) {
                        pendingInvoice.value = data
                        return true
                    } else {
                        localStorage.removeItem(PENDING_INVOICE_KEY)
                        return false
                    }
                }
                return false
            } catch (e) {
                localStorage.removeItem(PENDING_INVOICE_KEY)
                return false
            }
        }
        
        let pendingInvoiceData = null
        
        // Watch for external cart items
        if (props.externalCartItems !== null) {
            watch(() => props.externalCartItems, (newItems) => {
                cartItems.value = newItems
            }, { deep: true })
        }
        
        // Watch cart items
        watch(cartItems, (newItems) => {
            localStorage.setItem('shoppingCart', JSON.stringify(newItems))
            emit('cart-updated', {
                items: newItems,
                totalItems: cartTotalItems.value,
                totalAmount: cartTotalAmount.value
            })
        }, { deep: true })
        
        // Computed properties
        const cartTotalItems = computed(() => {
            return cartItems.value.reduce((total, item) => total + item.quantity, 0)
        })
        
        const cartTotalAmount = computed(() => {
            return cartItems.value.reduce((total, item) => total + (item.price * item.quantity), 0)
        })
        
        // ============= METHODS =============
        
        const toggleCart = () => {
            showCart.value = !showCart.value
            if (showCart.value) {
                checkForPendingInvoice()
            }
        }
        
        const closeCart = () => {
            showCart.value = false
        }
        
        const checkForPendingInvoice = async () => {
            if (pendingInvoice.value) {
                showCart.value = false
                const { invoiceId, ac, act } = pendingInvoice.value
                if (invoiceId) {
                    await showInvoiceConfirmation(invoiceId, ac || 2, act || 279)
                }
            }
        }
        
        const closeRegistrationModal = () => {
            showRegistrationModal.value = false
            registrationLoading.value = false
            pendingInvoiceData = null
        }
        
        // ============= INVOICE CONFIRMATION METHODS =============
        
        const showInvoiceConfirmation = async (invoiceId, ac, act) => {
            console.log('Showing confirmation for invoice: ' + invoiceId)
            showInvoiceConfirmationModal.value = true
            invoiceConfirmationLoading.value = true
            invoiceConfirmationError.value = null
            termsAccepted.value = false
            
            try {
                const response = await invoiceService.getInvoiceDetail(invoiceId, ac, act)
                console.log('Invoice detail response:', response.data)
                
                if (response.data && response.data.success) {
                    const data = response.data
                    const totalAmount = data.details?.reduce((sum, item) => sum + (item.money || 0), 0) || 0
                    
                    invoiceConfirmationData.value = {
                        Num: data.invoice_info?.invoice_id || data.invoice_id || invoiceId,
                        Dates: data.Dates || moment().format('jYYYY/jMM/jDD'),
                        details: data.details || [],
                        invoice_info: {
                            invoice_id: data.invoice_info?.invoice_id || data.invoice_id || invoiceId,
                            total_amount: data.invoice_info?.total_amount || totalAmount,
                            ac: data.invoice_info?.ac || ac,
                            act: data.invoice_info?.act || act
                        }
                    }
                    
                    pendingInvoiceId.value = invoiceId
                    console.log('Invoice confirmation data:', invoiceConfirmationData.value)
                } else {
                    invoiceConfirmationError.value = response.data?.message || 'خطا در دریافت جزئیات فاکتور'
                }
            } catch (error) {
                console.error('Error fetching invoice details:', error)
                invoiceConfirmationError.value = 'خطا در ارتباط با سرور. لطفاً دوباره تلاش کنید.'
            } finally {
                invoiceConfirmationLoading.value = false
            }
        }
        
        const closeInvoiceConfirmationModal = () => {
            showInvoiceConfirmationModal.value = false
            invoiceConfirmationData.value = null
            invoiceConfirmationError.value = null
            termsAccepted.value = false
            pendingInvoiceId.value = null
        }
        
        const confirmInvoice = async () => {
            if (!termsAccepted.value) return
            
            const invoiceId = pendingInvoiceId.value
            if (!invoiceId) {
                alert('شناسه فاکتور یافت نشد')
                return
            }
            
            invoiceConfirmationLoading.value = true
            
            try {
                const response = await SalesService.okInvoice(invoiceId)
                
                if (response.data.success) {
                    showToastMessage('فاکتور با موفقیت تایید شد!', 'success', '✅')
                    
                    emit('invoice-confirmed', {
                        invoiceId: invoiceId,
                        response: response.data
                    })
                    
                    savePendingInvoice(null)
                    cartItems.value = []
                    localStorage.removeItem('shoppingCart')
                    
                    closeInvoiceConfirmationModal()
                    closeCart()
                } else {
                    alert(`خطا در تایید فاکتور: ${response.data.message}`)
                }
            } catch (error) {
                console.error('Error confirming invoice:', error)
                alert('خطا در ارتباط با سرور. لطفاً دوباره تلاش کنید.')
            } finally {
                invoiceConfirmationLoading.value = false
            }
        }
        
        const fetchInvoiceDetails = async () => {
            if (pendingInvoiceId.value) {
                await showInvoiceConfirmation(pendingInvoiceId.value, 2, 279)
            }
        }
        
        // ============= CART METHODS =============
        
        const addToCart = (product, quantity = 1) => {
            savePendingInvoice(null)
            
            const existingItem = cartItems.value.find(item => item.kala_id === product.SubCode)
            
            if (existingItem) {
                existingItem.quantity += quantity
            } else {
                cartItems.value.push({
                    kala_id: product.SubCode,
                    name: product.Name,
                    price: parseFloat(product.Fe1 || 0),
                    quantity: quantity,
                    unit: product.Vahed1,
                    Vahed1: product.Vahed1,
                    Vahed2: product.Vahed2,
                    Kala4: product.Kala4,
                    Commodity_Code: product.Commodity_Code,
                    MainCode: product.MainCode,
                    max_quantity: product.StockQuantity || 999,
                    image_url: product.Image1 || null,
                    note: ''
                })
            }
        }
        
        const removeFromCart = (item) => {
            cartItems.value = cartItems.value.filter(cartItem => cartItem.kala_id !== item.kala_id)
        }
        
        const increaseQuantity = (item) => {
            item.quantity++
        }
        
        const decreaseQuantity = (item) => {
            if (item.quantity > 1) {
                item.quantity--
            }
        }
        
        const updateQuantity = (item) => {
            if (item.quantity < 1) {
                item.quantity = 1
            }
        }
        
        const updateNote = () => {
            localStorage.setItem('shoppingCart', JSON.stringify(cartItems.value))
            emit('cart-updated', {
                items: cartItems.value,
                totalItems: cartTotalItems.value,
                totalAmount: cartTotalAmount.value
            })
        }
        
        const clearCart = () => {
            if (confirm('آیا از پاک کردن سبد خرید مطمئن هستید؟')) {
                cartItems.value = []
                localStorage.removeItem('shoppingCart')
                savePendingInvoice(null)
            }
        }
        
        // ============= CHECKOUT METHODS =============
        
        const proceedToCheckout = async () => {
            if (cartItems.value.length === 0) {
                showToastMessage('سبد خرید شما خالی است', 'error', '❌')
                return
            }
            
            isProcessing.value = true
            
            try {
                const invoiceData = buildInvoiceData(cartItems.value)
                pendingInvoiceData = invoiceData
                
                console.log('Sending invoice data:', invoiceData)
                
                const response = await invoiceService.saveInvoice(invoiceData)
                
                console.log('Invoice response:', response.data)
                
                if (response.data.success) {
                    const invoiceId = response.data.invoice_number || null
                    const ac = invoiceData.Header.Ac || 2
                    const act = invoiceData.Header.Act || 279
                    
                    showToastMessage('سفارش با موفقیت ثبت شد!', 'success', '✅')
                    
                    emit('invoice-saved', {
                        invoiceData: invoiceData,
                        response: response.data
                    })
                    
                    closeCart()
                    
                    if (invoiceId) {
                        savePendingInvoice({
                            invoiceId: invoiceId,
                            ac: ac,
                            act: act,
                            timestamp: Date.now()
                        })
                        await showInvoiceConfirmation(invoiceId, ac, act)
                    } else {
                        const tempId = Date.now()
                        savePendingInvoice({
                            invoiceId: tempId,
                            ac: ac,
                            act: act,
                            timestamp: Date.now()
                        })
                        showManualConfirmation(invoiceData, response.data)
                    }
                    
                } else {
                    if (response.data.return_code === 403) {
                        showRegistrationModal.value = true
                        const user = JSON.parse(localStorage.getItem('user') || '{}')
                        registrationData.value.phone = user.phone || ''
                        showToastMessage('لطفاً ابتدا ثبت نام کنید', 'warning', '⚠️')
                    } else {
                        showToastMessage(response.data.message || 'خطا در ثبت سفارش', 'error', '❌')
                    }
                    
                    emit('checkout', {
                        items: cartItems.value,
                        success: false,
                        error: response.data.message
                    })
                }
            } catch (error) {
                console.error('Checkout error:', error)
                
                let errorMessage = 'خطا در ارتباط با سرور'
                if (error.response?.data?.detail) {
                    errorMessage = error.response.data.detail
                } else if (error.message) {
                    errorMessage = error.message
                }
                
                if (errorMessage.includes('not registered') || errorMessage.includes('User not registered')) {
                    showRegistrationModal.value = true
                    const user = JSON.parse(localStorage.getItem('user') || '{}')
                    registrationData.value.phone = user.phone || ''
                    showToastMessage('لطفاً ابتدا ثبت نام کنید', 'warning', '⚠️')
                } else {
                    showToastMessage('خطا در ارتباط با سرور', 'error', '❌')
                }
                
                emit('checkout', {
                    items: cartItems.value,
                    success: false,
                    error: errorMessage
                })
            } finally {
                isProcessing.value = false
            }
        }
        
        const showManualConfirmation = (invoiceData, response) => {
            showInvoiceConfirmationModal.value = true
            invoiceConfirmationLoading.value = false
            termsAccepted.value = false
            
            invoiceConfirmationData.value = {
                details: invoiceData.Body.map(item => ({
                    KalaName: `کد کالا: ${item.Commodity_code}`,
                    value1: item.value1,
                    Fe: item.Fe,
                    money: item.value1 * item.Fe
                })),
                Num: response.invoice_number || 'جدید',
                Dates: invoiceData.Header.Dates,
                invoice_info: {
                    invoice_id: response.invoice_id || Date.now(),
                    total_amount: calculateTotalAmount(invoiceData.Body)
                }
            }
            
            pendingInvoiceId.value = response.invoice_id || Date.now()
        }
        
        const calculateTotalAmount = (body) => {
            return body.reduce((total, item) => total + (item.value1 * item.Fe), 0)
        }
        
        // ============= REGISTRATION METHODS =============
        
        const submitRegistration = async () => {
            if (!registrationData.value.name.trim()) {
                showToastMessage('لطفاً نام و نام خانوادگی را وارد کنید', 'error', '❌')
                return
            }
            
            if (!registrationData.value.phone.trim()) {
                showToastMessage('لطفاً شماره تلفن را وارد کنید', 'error', '❌')
                return
            }
            
            registrationLoading.value = true
            
            try {
                const response = await authService.registerCustomer(registrationData.value)
                
                if (response.data.success) {
                    showToastMessage('ثبت نام با موفقیت انجام شد!', 'success', '✅')
                    
                    closeRegistrationModal()
                    emit('customer-registered', registrationData.value)
                    
                    if (pendingInvoiceData) {
                        showToastMessage('در حال ثبت سفارش...', 'warning', '⏳')
                        setTimeout(async () => {
                            await retryInvoice()
                        }, 1000)
                    }
                } else {
                    showToastMessage(response.data.message || 'خطا در ثبت نام', 'error', '❌')
                }
            } catch (error) {
                console.error('Registration error:', error)
                showToastMessage('خطا در ثبت نام. لطفاً دوباره تلاش کنید.', 'error', '❌')
            } finally {
                registrationLoading.value = false
            }
        }
        
        const retryInvoice = async () => {
            if (!pendingInvoiceData) return
            
            isProcessing.value = true
            
            try {
                const response = await invoiceService.saveInvoice(pendingInvoiceData)
                
                if (response.data.success) {
                    showToastMessage('سفارش با موفقیت ثبت شد!', 'success', '✅')
                    
                    emit('invoice-saved', {
                        invoiceData: pendingInvoiceData,
                        response: response.data
                    })
                    
                    const invoiceId = response.data.invoice_number || null
                    const ac = pendingInvoiceData.Header.Ac || 2
                    const act = pendingInvoiceData.Header.Act || 279
                    
                    closeCart()
                    
                    if (invoiceId) {
                        savePendingInvoice({
                            invoiceId: invoiceId,
                            ac: ac,
                            act: act,
                            timestamp: Date.now()
                        })
                        await showInvoiceConfirmation(invoiceId, ac, act)
                    } else {
                        const tempId = Date.now()
                        savePendingInvoice({
                            invoiceId: tempId,
                            ac: ac,
                            act: act,
                            timestamp: Date.now()
                        })
                        showManualConfirmation(pendingInvoiceData, response.data)
                    }
                    
                    pendingInvoiceData = null
                } else {
                    showToastMessage(response.data.message || 'خطا در ثبت سفارش', 'error', '❌')
                }
            } catch (error) {
                console.error('Retry error:', error)
                showToastMessage('خطا در ثبت سفارش. لطفاً دوباره تلاش کنید.', 'error', '❌')
            } finally {
                isProcessing.value = false
            }
        }
        
        // ============= HELPER METHODS =============
        
        const buildInvoiceData = (cartItems) => {
            const persianDate = moment().format('jYYYY/jMM/jDD')
            const user = JSON.parse(localStorage.getItem('user') || '{}')
            
            // Get accountant-specific IDs if in accountant mode
            let operatorId = user.id || 0
            let customerId = user.id || 0
            
            if (isAccountantMode.value) {
                const accountantOperatorId = getAccountantOperatorId()
                const accountantCustomerId = getAccountantCustomerId()
                
                if (accountantOperatorId) {
                    operatorId = parseInt(accountantOperatorId)
                }
                if (accountantCustomerId) {
                    customerId = parseInt(accountantCustomerId)
                }else {
                    customerId = null
                }
                console.log('Accountant mode - Operator ID:', operatorId, 'Customer ID:', customerId)
            }
            
            return {
                Header: {
                    Ac: 2,
                    Act: 279,
                    Dates: persianDate,
                    Ei5: customerId,
                    Operator: operatorId,
                    Arz: 1,
                    Erja: 0,
                    Duration: 0,
                    Saresid: '',
                    Comments: '',
                    Pelak: '',
                    In_Oto: '',
                    In_Bar: '',
                    In_Driver: 0,
                    In_Fe: 0,
                    To_Time: 0,
                    Rec_num: 0,
                    Rec_Date: ''
                },
                Body: cartItems.map((item) => ({
                    Codeing: 1,
                    Commodity_code: parseInt(item.kala_id),
                    value1: item.quantity,
                    Fe: item.price || 0,
                    Precent: 0,
                    Discount: 0,
                    DScot: 0,
                    Scot: 0,
                    DToll: 0,
                    Toll: 0,
                    Depot: 6
                })),
                Dec: [],
                Inc: [],
                Payment: []
            }
        }
        
        const showToastMessage = (message, type = 'success', icon = '✅') => {
            toastMessage.value = message
            toastType.value = type
            toastIcon.value = icon
            showToast.value = true
            
            if (window.toastTimeout) {
                clearTimeout(window.toastTimeout)
            }
            
            window.toastTimeout = setTimeout(() => {
                showToast.value = false
            }, 5000)
        }
        
        const formatCurrency = (amount) => {
            return new Intl.NumberFormat('fa-IR').format(amount)
        }
        
        const formatDate = (dateStr) => {
            if (!dateStr) return '-'
            if (dateStr.includes('/')) return dateStr
            try {
                const date = new Date(dateStr)
                if (isNaN(date.getTime())) return dateStr
                return new Intl.DateTimeFormat('fa-IR', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit'
                }).format(date)
            } catch {
                return dateStr
            }
        }
        
        const isInCart = (product) => {
            return cartItems.value.some(item => item.kala_id === product.SubCode)
        }
        
        const getCartItemCount = () => {
            return cartTotalItems.value
        }

        const updateCartItemQuantity = (product, newQuantity) => {
            const itemKey = product.SubCode
            const existingItem = cartItems.value.find(item => item.kala_id === itemKey)
            
            if (existingItem) {
                if (newQuantity <= 0) {
                    cartItems.value = cartItems.value.filter(item => item.kala_id !== itemKey)
                } else {
                    existingItem.quantity = newQuantity
                }
                localStorage.setItem('shoppingCart', JSON.stringify(cartItems.value))
                emit('cart-updated', {
                    items: cartItems.value,
                    totalItems: cartTotalItems.value,
                    totalAmount: cartTotalAmount.value
                })
            }
        }

        const getProductUnit = (product) => {
            if (product.Kala4 === "1.0" || product.Kala4 === 1) {
                return product.Vahed2 || product.Vahed1 || 'عدد'
            }
            return product.Vahed1 || 'عدد'
        }
        
        // ============= LIFECYCLE =============
        onMounted(() => {
            loadPendingInvoice()
            
            // Log accountant mode status
            if (isAccountantMode.value) {
                console.log('Accountant mode detected')
                console.log('Operator ID:', getAccountantOperatorId())
                console.log('Customer ID:', getAccountantCustomerId())
            }
        })
   
        return {
            cartItems,
            showCart,
            isProcessing,
            showToast,
            toastMessage,
            toastType,
            toastIcon,
            cartTotalItems,
            cartTotalAmount,
            showRegistrationModal,
            registrationData,
            registrationLoading,
            showInvoiceConfirmationModal,
            invoiceConfirmationLoading,
            invoiceConfirmationData,
            invoiceConfirmationError,
            termsAccepted,
            pendingInvoice,
            hasPendingInvoice,
            isAccountantMode,
            toggleCart,
            closeCart,
            addToCart,
            removeFromCart,
            increaseQuantity,
            decreaseQuantity,
            updateQuantity,
            updateNote,
            clearCart,
            proceedToCheckout,
            submitRegistration,
            closeRegistrationModal,
            closeInvoiceConfirmationModal,
            confirmInvoice,
            formatCurrency,
            formatDate,
            isInCart,
            getCartItemCount,
            updateCartItemQuantity,
            getProductUnit,
            showToastMessage,
            fetchInvoiceDetails,
            savePendingInvoice,
            loadPendingInvoice,
            getAccountantOperatorId,
            getAccountantCustomerId
        }
    }
}
</script>

<style scoped>
/* Shopping Cart */
.cart-floating {
    position: fixed;
    bottom: 50px;
    left: 20px;
    background: linear-gradient(135deg, var(--gradient-1) 0%, var(--gradient-2) 100%);
    color: white;
    width: 60px;
    height: 60px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 4px 12px var(--shadow-2);
    z-index: 1000;
    transition: transform 0.3s;
}

.cart-floating:hover {
    transform: scale(1.1);
}

.cart-icon {
    font-size: 24px;
    position: relative;
}

.cart-badge {
    position: absolute;
    top: -5px;
    right: -5px;
    background: var(--orange-1);
    color: white;
    font-size: 12px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
}

/* Cart Modal */
.cart-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: var(--modal-overlay);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
    padding: 20px;
}

.cart-modal-content {
    background: var(--card-bg);
    border-radius: 12px;
    width: 100%;
    max-width: 500px;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    border: 1px solid var(--card-border);
}

.cart-modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    border-bottom: 1px solid var(--card-border);
}

.cart-modal-header h3 {
    color: var(--heading);
}

.close-cart-btn {
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: var(--subtext);
    transition: color 0.3s;
}

.close-cart-btn:hover {
    color: var(--orange-1);
}

.cart-modal-body {
    flex: 1;
    overflow-y: auto;
    padding: 20px;
}

.empty-cart {
    text-align: center;
    padding: 40px 20px;
    color: var(--subtext);
}

.continue-shopping {
    background: var(--primary);
    color: white;
    border: none;
    padding: 10px 20px;
    border-radius: 6px;
    margin-top: 15px;
    cursor: pointer;
    transition: all 0.3s;
}

.continue-shopping:hover {
    background: var(--primary-hover);
}

.cart-item {
    padding: 15px;
    border-bottom: 1px solid var(--card-border);
}

.cart-item-info {
    margin-bottom: 10px;
}

.cart-item-name {
    font-weight: bold;
    margin-bottom: 5px;
    color: var(--heading);
    text-align: right;
}

.cart-item-price {
    color: var(--primary);
    font-weight: bold;
}

.cart-item-controls {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
}

.quantity-btn {
    background: var(--bg);
    border: 1px solid var(--card-border);
    width: 30px;
    height: 30px;
    border-radius: 4px;
    cursor: pointer;
    color: var(--text);
    transition: all 0.3s;
}

.quantity-btn:hover {
    background: var(--ring);
    border-color: var(--primary);
}

.quantity-input {
    width: 50px;
    text-align: center;
    padding: 5px;
    border: 1px solid var(--card-border);
    border-radius: 4px;
    background: var(--bg);
    color: var(--text);
}

.remove-item-btn {
    background: var(--orange-1);
    color: white;
    border: none;
    padding: 5px 10px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
    transition: background 0.3s;
}

.remove-item-btn:hover {
    background: var(--orange-2);
}

.cart-item-total {
    text-align: left;
    font-weight: bold;
    color: var(--heading);
}

.cart-summary {
    padding: 20px;
    background: var(--bg);
    border-radius: 8px;
    margin-top: 20px;
    border: 1px solid var(--card-border);
}

.summary-row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 10px;
    color: var(--text);
}

.summary-row.total {
    font-size: 1.2rem;
    font-weight: bold;
    color: var(--primary);
    border-top: 2px solid var(--card-border);
    padding-top: 10px;
    margin-top: 10px;
}

.cart-modal-footer {
    display: flex;
    gap: 10px;
    padding: 20px;
    border-top: 1px solid var(--card-border);
}

.clear-cart-btn {
    flex: 1;
    background: var(--orange-1);
    color: white;
    border: none;
    padding: 12px;
    border-radius: 6px;
    cursor: pointer;
    transition: background 0.3s;
}

.clear-cart-btn:hover {
    background: var(--orange-2);
}

.checkout-btn {
    flex: 2;
    background: var(--green);
    color: white;
    border: none;
    padding: 12px;
    border-radius: 6px;
    cursor: pointer;
    font-weight: bold;
    transition: background 0.3s;
}

.checkout-btn:hover {
    background: #0d9f6e;
}

/* Responsive */
@media (max-width: 768px) {
    .cart-modal-content {
        max-width: 100%;
    }
}
.remove-from-cart-btn {
    width: 24px;
    height: 24px;
    border: none;
    border-radius: 50%;
    background: rgba(249, 115, 22, 0.1);
    color: var(--orange-1);
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    line-height: 1;
}

.remove-from-cart-btn:hover {
    background: rgba(249, 115, 22, 0.2);
    transform: scale(1.1);
}
.product-unit{
    font-size: 12px;
    color: var(--subtext);
}
.cart-item-note {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 8px 0;
    padding: 4px 0;
    
    padding-top: 8px;
}

.note-input {
    flex: 1;
    padding: 6px 35px 6px 12px;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    font-size: 13px;
    direction: rtl;
    background: var(--bg);
    color: var(--text);
    transition: all 0.3s ease;
    min-width: 0;
}

.note-input:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
    background: var(--bg);
}

.note-input::placeholder {
    color: #999;
    font-size: 12px;
}

.note-icon {
    position: relative;
    right: -30px;
    font-size: 14px;
    opacity: 0.6;
    pointer-events: none;
    margin-right: -30px;
}

/* Adjust the cart item spacing */
.cart-item {
    padding: 15px;
    border-bottom: 1px solid var(--card-border);
    margin-bottom: 5px;
}

.cart-item:last-child {
    border-bottom: none;
}

/* Make the remove button more visible */
.remove-from-cart-btn {
    background: #f44336;
    color: white;
    border: none;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    cursor: pointer;
    font-size: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    padding: 0;
    line-height: 1;
}

.remove-from-cart-btn:hover {
    background: #d32f2f;
    transform: scale(1.1);
}

/* Responsive adjustments */
@media (max-width: 768px) {
    .cart-item-controls {
        flex-wrap: wrap;
        gap: 8px;
    }
    
    .cart-item-controls .quantity-btn {
        width: 32px;
        height: 32px;
        font-size: 16px;
    }
    
    .cart-item-controls .quantity-input {
        width: 45px;
        padding: 4px;
    }
    
    .note-input {
        font-size: 12px;
        padding: 5px 30px 5px 10px;
    }
}
.toast-notification {
    position: sticky;
    bottom: 20px;
    margin: 10px 20px;
    padding: 15px 25px;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 20;
    animation: slideUp 0.5s ease-out;
    width: calc(100% - 40px);
}

.toast-notification.success {
    background: linear-gradient(135deg, #4CAF50, #45a049);
    color: white;
    border-right: 4px solid #2e7d32;
}

.toast-notification.error {
    background: linear-gradient(135deg, #f44336, #d32f2f);
    color: white;
    border-right: 4px solid #b71c1c;
}

.toast-notification.warning {
    background: linear-gradient(135deg, #ff9800, #f57c00);
    color: white;
    border-right: 4px solid #e65100;
}

.toast-content {
    display: flex;
    align-items: center;
    gap: 12px;
}

.toast-icon {
    font-size: 24px;
    flex-shrink: 0;
}

.toast-message {
    flex: 1;
    font-size: 15px;
    font-weight: 500;
    line-height: 1.4;
}

.toast-close {
    background: none;
    border: none;
    color: white;
    font-size: 24px;
    cursor: pointer;
    padding: 0 8px;
    opacity: 0.8;
    transition: opacity 0.2s;
    flex-shrink: 0;
}

.toast-close:hover {
    opacity: 1;
}

@keyframes slideUp {
    from {
        opacity: 0;
        transform: translateY(30px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.registration-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: var(--modal-overlay);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    animation: fadeIn 0.3s ease-out;
}

.registration-modal-content {
    background: var(--card-bg);
    border-radius: 12px;
    max-width: 500px;
    width: 95%;
    max-height: 90vh;
    overflow-y: auto;
    animation: slideUp 0.3s ease-out;
    border: 1px solid var(--card-border);
    box-shadow: 0 8px 32px var(--shadow-2);
}

.registration-modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    border-bottom: 1px solid var(--card-border);
}

.registration-modal-header h3 {
    margin: 0;
    font-size: 18px;
    color: var(--heading);
    font-weight: 600;
}

.close-modal-btn {
    background: none;
    border: none;
    font-size: 28px;
    color: var(--subtext);
    cursor: pointer;
    padding: 0 8px;
    transition: color 0.2s;
}

.close-modal-btn:hover {
    color: var(--orange-1);
    transform: rotate(90deg);
}

.registration-modal-body {
    padding: 24px;
}

.registration-message {
    background: rgba(249, 115, 22, 0.1);
    border: 1px solid var(--orange-1);
    border-radius: 8px;
    padding: 15px;
    margin-bottom: 20px;
}

.registration-message p {
    margin: 5px 0;
    color: var(--orange-1);
}

.registration-form {
    display: flex;
    flex-direction: column;
    gap: 18px;
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.form-group label {
    font-weight: 500;
    color: var(--heading);
    font-size: 14px;
}

.form-group label .required {
    color: var(--orange-1);
}

.form-input,
.form-textarea {
    padding: 10px 12px;
    border: 1px solid var(--card-border);
    border-radius: 6px;
    font-size: 14px;
    transition: border-color 0.2s, box-shadow 0.2s;
    font-family: inherit;
    background: var(--bg);
    color: var(--text);
}

.form-input:focus,
.form-textarea:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px var(--ring);
}

.form-input[readonly] {
    background-color: var(--bg);
    color: var(--subtext);
    opacity: 0.7;
    cursor: not-allowed;
}

.form-hint {
    font-size: 12px;
    color: var(--muted);
}

.form-actions {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    margin-top: 8px;
}

.cancel-btn {
    padding: 10px 20px;
    background: var(--bg);
    border: 1px solid var(--card-border);
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;
    transition: all 0.2s;
    color: var(--subtext);
}

.cancel-btn:hover {
    background: var(--hover);
    border-color: var(--muted);
}

.register-btn {
    padding: 10px 24px;
    background: var(--green);
    color: white;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;
    transition: all 0.2s;
}

.register-btn:hover:not(:disabled) {
    background: #0d9f6e;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.register-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
}

/* Toast Styles */
.toast-notification {
    position: sticky;
    bottom: 20px;
    margin: 10px 20px;
    padding: 15px 25px;
    border-radius: 8px;
    box-shadow: 0 4px 12px var(--shadow-2);
    z-index: 20;
    animation: slideUp 0.5s ease-out;
}

.toast-notification.success {
    background: linear-gradient(135deg, var(--green), #0d9f6e);
    color: white;
}

.toast-notification.error {
    background: linear-gradient(135deg, var(--orange-1), var(--orange-2));
    color: white;
}

.toast-notification.warning {
    background: linear-gradient(135deg, #f59e0b, #d97706);
    color: white;
}

.toast-content {
    display: flex;
    align-items: center;
    gap: 12px;
}

.toast-icon {
    font-size: 24px;
}

.toast-message {
    flex: 1;
    font-size: 15px;
    font-weight: 500;
}

.toast-close {
    background: none;
    border: none;
    color: white;
    font-size: 24px;
    cursor: pointer;
    padding: 0 8px;
    opacity: 0.8;
    transition: opacity 0.2s;
}

.toast-close:hover {
    opacity: 1;
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}

@keyframes slideUp {
    from {
        opacity: 0;
        transform: translateY(30px) scale(0.95);
    }
    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

/* Responsive Adjustments */
@media (max-width: 480px) {
    .registration-modal-content {
        width: 100%;
        max-height: 100vh;
        border-radius: 0;
    }
    
    .registration-modal-header {
        padding: 16px 20px;
    }
    
    .registration-modal-body {
        padding: 20px;
    }
    
    .form-actions {
        flex-direction: column;
    }
    
    .cancel-btn,
    .register-btn {
        width: 100%;
        justify-content: center;
    }
    
    .toast-notification {
        margin: 10px;
        padding: 12px 16px;
    }
}

/* Existing styles... */
.cart-modal-body {
    position: relative;
    max-height: 60vh;
    overflow-y: auto;
    padding: 20px;
    flex: 1;
}
.invoice-confirmation-modal {
    width: 92%;
    max-width: 600px;
    background: var(--card-bg);
    border-radius: 16px;
    padding: 0;
    box-shadow: 0 8px 32px rgba(0,0,0,0.25);
    max-height: 90vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.invoice-confirmation-modal .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 24px;
    border-bottom: 1px solid var(--border);
}

.invoice-confirmation-modal .modal-body {
    padding: 20px 24px;
    overflow-y: auto;
    flex: 1;
}

.confirmation-summary {
    background: var(--bg);
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 20px;
    border: 1px solid var(--border);
}

.confirmation-summary .summary-row {
    display: flex;
    justify-content: space-between;
    padding: 8px 0;
    border-bottom: 1px solid var(--border);
}

.confirmation-summary .summary-row:last-child {
    border-bottom: none;
}

.confirmation-summary .summary-row .label {
    color: var(--text-muted);
    font-size: 14px;
}

.confirmation-summary .summary-row .value {
    color: var(--text);
    font-weight: 500;
    font-size: 14px;
}

.confirmation-summary .summary-row.total .value {
    font-size: 18px;
    font-weight: 700;
    color: var(--green);
}

.confirmation-items {
    margin-bottom: 20px;
}

.confirmation-items h4 {
    margin: 0 0 12px 0;
    font-size: 16px;
    color: var(--heading);
}

.items-list {
    max-height: 200px;
    overflow-y: auto;
    border: 1px solid var(--border);
    border-radius: 8px;
}

.items-list .item-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 12px;
    border-bottom: 1px solid var(--border);
}

.items-list .item-row:last-child {
    border-bottom: none;
}

.items-list .item-name {
    flex: 1;
    font-size: 14px;
    color: var(--text);
}

.items-list .item-details {
    font-size: 13px;
    color: var(--text-muted);
    margin: 0 12px;
}

.items-list .item-total {
    font-size: 14px;
    font-weight: 600;
    color: var(--green);
}

.terms-section {
    padding: 12px 0;
    border-top: 1px solid var(--border);
}

.terms-checkbox {
    display: flex;
    align-items: center;
    gap: 12px;
    cursor: pointer;
    font-size: 14px;
    color: var(--text);
}

.terms-checkbox input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
    accent-color: var(--primary);
}

.terms-text {
    color: var(--text);
    font-weight: 500;
}

.invoice-confirmation-modal .modal-footer {
    padding: 16px 24px;
    border-top: 1px solid var(--border);
    display: flex;
    justify-content: flex-end;
    gap: 12px;
}

.confirm-btn {
    padding: 10px 24px;
    background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
    color: white;
    border: none;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
}

.confirm-btn:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(34, 197, 94, 0.4);
}

.confirm-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
}

.confirm-btn .spinner-small {
    display: inline-block;
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255,255,255,0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}
</style>